/* eslint-disable indent */
import React, { forwardRef, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Download, Edit, Expand, Eye, EyeOff, Heart, ImageMinus, Lightbulb, MergeIcon, MoreHorizontal, Move, Music, Music2, RefreshCw, Share2, ShieldAlert, SmilePlus, Trash2, Trophy, Video, Wand2 } from "lucide-react";
import { useRouter } from "next/router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AppContext } from "@/lib/contexts";
import { AppReducerActionType, ImageDTO } from "@/types";

// Fix the debounce function formatting
const debounce = (fn: Function, ms = 300) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function (...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), ms);
  };
};
interface ControlsContainerProps {
  isFirstImage?: boolean;
  imageInfo: ImageDTO;
  isAdmin: boolean;
  handleToggle: (id: string) => void;
  handleToggleNSFW: (id: string) => void;
  handleShareClick: (e: React.MouseEvent, imageId?: string) => void;
  handleCompetitionsClick: (imageId?: string) => void;
  handleEditClick: (imageId?: string) => void;
  handleADetailerClick: (imageId?: string) => void;
  handleFaceSwapClick: (imageId?: string) => void;
  handleBGRemovalClick: (imageId?: string) => void;
  handleInpaintClick: (imageId?: string) => void;
  handleUpscaleClick: (imageId?: string) => void;
  handleAudioClick: (imageId?: string) => void;
  handleQuickEditClick: (imageId?: string) => void;
  prepareForDelete: (id: string) => void;
  handleVote: (id: string) => void;
  isUserImage: (username: string) => boolean;
  showLabel?: boolean;
}
const ControlsContainer: React.FC<ControlsContainerProps> = ({
  isFirstImage,
  imageInfo,
  isAdmin,
  handleToggle,
  handleToggleNSFW,
  handleShareClick,
  handleCompetitionsClick,
  handleADetailerClick,
  handleFaceSwapClick,
  handleBGRemovalClick,
  handleInpaintClick,
  handleUpscaleClick,
  handleAudioClick,
  handleQuickEditClick,
  prepareForDelete,
  handleVote,
  isUserImage,
  showLabel = false
}) => {
  const {
    dispatch,
    state
  } = useContext(AppContext);
  const controlsRef = useRef<HTMLDivElement>(null);
  const [visibleControls, setVisibleControls] = useState<any[]>([]);
  const [overflowControls, setOverflowControls] = useState<any[]>([]);
  const isOwnedByUser = isUserImage(imageInfo.imageMetadata.username);
  const isVideo = imageInfo.image.endsWith(".mp4");
  const isAudio = imageInfo.image.endsWith(".mp3");
  const popoverButtonRef = useRef<HTMLButtonElement>(null);
  interface ControlButtonProps {
    icon: React.ComponentType<{
      className?: string;
    }>;
    onClick: (e: React.MouseEvent) => void;
    tooltip: string;
    className?: string;
    label?: string;
    showLabel?: boolean;
  }
  const router = useRouter();
  const handleUseAsReferenceClick = useCallback((image?: string) => {
    if (image) {
      if (["/generate", "/character", "/npc", "/monster", "/landscape", "/item", "/battlemap"].includes(router.pathname)) {
        dispatch({
          type: AppReducerActionType.SET_PENDING_REFERENCE_IMAGE,
          payload: {
            ...state,
            pendingReferenceImageUrl: image
          }
        });
        document.querySelector("#image-creation-card")?.scrollIntoView({
          behavior: "smooth"
        });
        toast.info("Reference image updated in Generate Form.");
      } else {
        router.push({
          pathname: "/generate",
          query: {
            imageReference: image
          }
        });
      }
    }
  }, [router, dispatch, state]);
  const handleOutpaintingClick = useCallback((image?: ImageDTO) => {
    if (image) {
      router.push({
        pathname: "/edit",
        query: {
          editMode: "Outpainting",
          imageId: image.id
        }
      }).then(() => window.location.reload());
    }
  }, [router]);
  const handleVideoClick = useCallback((image?: ImageDTO) => {
    if (image) {
      router.push({
        pathname: "/edit",
        query: {
          editMode: "Animate",
          imageId: image.id
        }
      }).then(() => window.location.reload());
    }
  }, [router]);
  const handleLipSyncClick = useCallback((image?: ImageDTO) => {
    if (image) {
      router.push({
        pathname: "/edit",
        query: {
          editMode: "Lip Sync",
          imageId: image.id
        }
      }).then(() => window.location.reload());
    }
  }, [router]);
  const handleMergeAudioAndVideoClick = useCallback((image?: ImageDTO) => {
    if (image) {
      router.push({
        pathname: "/edit",
        query: {
          editMode: "Merge Audio & Video",
          imageId: image.id
        }
      }).then(() => window.location.reload());
    }
  }, [router]);

  // Modify the ControlButton component to have a consistent width when showing labels
  const ControlButton = forwardRef<HTMLDivElement, ControlButtonProps>(({
    icon: Icon,
    onClick,
    tooltip,
    className = "",
    label,
    showLabel = false
  }, ref) => <div ref={ref} className="px-0.5">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className={`${showLabel ? "!w-auto !px-3 !min-w-[70px] sm:!min-w-[80px] justify-start" : "!w-9"} !h-9 !rounded-lg !text-slate-200 hover:!text-sky-400 hover:!bg-sky-400/20 transition-colors duration-200 ${className}`} onClick={onClick} aria-label={label} aria-expanded={showLabel}>
                            <Icon className="!h-5 !w-5 !flex-shrink-0" />
                            {label && showLabel && <span className="ml-0 whitespace-nowrap !text-xs overflow-hidden text-ellipsis max-w-[120px]">
                                    {label}
                                </span>}
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-64 z-[2000]">
                        {tooltip}
                    </TooltipContent>
                </Tooltip>
            </div>);
  // Add displayName to the component
  ControlButton.displayName = "ControlButton";

  // Initialize refs for fixed controls
  const fixedControlRefs = useRef<{
    [key: string]: HTMLDivElement | null;
  }>({});

  // Initialize refs for expandable controls
  const expandableControlRefs = useRef<{
    [key: string]: HTMLDivElement | null;
  }>({});

  // Define fixed controls data
  const fixedControls = useMemo(() => [{
    key: "like",
    render: (ref: React.Ref<HTMLDivElement>) => <div ref={ref}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button variant="ghost" size="sm" className={`!flex !items-center !gap-1 !px-2 !rounded-lg !transition-colors !duration-200 ${imageInfo.votedByUser ? "!bg-rose-500/20 !text-rose-500 hover:!text-rose-400" : "!text-slate-200 hover:!text-sky-400 hover:!bg-slate-800/50"}`} onClick={e => {
            e.stopPropagation();
            handleVote(imageInfo.id);
          }} aria-label="Like this image">
                                    <Heart className={`h-4 w-4 mr-2 ${imageInfo.votedByUser ? "fill-rose-500" : ""}`} />
                                    <span>{imageInfo.voteCount}</span>
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                {imageInfo.votedByUser ? "Unlike this image" : "Like this image"}
                            </TooltipContent>
                        </Tooltip>
                    </div>
  },
  // Only show the view count if showLabel is true (in detail view)
  ...(showLabel ? [{
    key: "views",
    render: (ref: React.Ref<HTMLDivElement>) => <div ref={ref}>
                                  <Tooltip>
                                      <TooltipTrigger asChild>
                                          <Button variant="ghost" size="sm" className="!flex !items-center !gap-1 !px-2 !rounded-lg !text-slate-200 hover:!text-sky-400 hover:!bg-slate-800/50 !transition-colors !duration-200" aria-label="View count">
                                              <Eye className="h-4 w-4 mr-2" />
                                              <span>
                                                  {imageInfo.viewCount || 0}
                                              </span>
                                          </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                          Total views
                                      </TooltipContent>
                                  </Tooltip>
                              </div>
  }] : []), {
    key: "share",
    render: (ref: React.Ref<HTMLDivElement>) => <ControlButton ref={ref} icon={Share2} onClick={e => handleShareClick(e, imageInfo.id)} tooltip="Share this image and earn Gold for each share." className={isFirstImage ? "joyride-share-button" : ""} showLabel={showLabel} label="Share & Earn Gold" />
  }, ...(isOwnedByUser || isAdmin ? [{
    key: "visibility",
    render: (ref: React.Ref<HTMLDivElement>) => <div ref={ref}>
                                  <Tooltip>
                                      <TooltipTrigger asChild>
                                          <Button variant="ghost" size="icon" className={`h-8 w-8 transition-colors duration-200 ${imageInfo.public ? "!text-sky-500 hover:!text-sky-400" : "!text-slate-200 hover:!text-slate-100"} ${isFirstImage ? "joyride-public-toggle" : ""}`} onClick={e => {
            e.stopPropagation();
            handleToggle(imageInfo.id);
          }} aria-label="Toggle Visibility">
                                              {imageInfo.public ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                          </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                          {imageInfo.public ? "Make private" : "Make public"}
                                      </TooltipContent>
                                  </Tooltip>
                              </div>
  }] : [])],
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [imageInfo.id, imageInfo.public, imageInfo.votedByUser, imageInfo.voteCount, imageInfo.viewCount, isOwnedByUser, handleToggle, handleShareClick, isAdmin]);

  // Define expandable controls data
  const expandableControls = useMemo(() => [{
    key: "download",
    label: "Download",
    icon: Download,
    onClick: () => {
      window.open(imageInfo.image, "_blank");
    },
    tooltip: "Download",
    className: isFirstImage ? "joyride-download-option" : ""
  }, ...(isAdmin ? [{
    key: "toggleNsfw",
    label: "Mark NSFW",
    icon: ShieldAlert,
    onClick: () => handleToggleNSFW(imageInfo.id),
    tooltip: "Mark this image as Not Safe for Work (NSFW)",
    className: "!text-red-500"
  }] : []), ...(isAudio ? [{
    key: "lipSync",
    label: "Lip Sync",
    icon: Music2,
    onClick: () => handleLipSyncClick(imageInfo),
    tooltip: "Add a Video to this Audio",
    className: ""
  }, {
    key: "mergeAudioAndVideo",
    label: "Merge Audio & Video",
    icon: MergeIcon,
    onClick: () => handleMergeAudioAndVideoClick(imageInfo),
    tooltip: "Merge this Audio with a Video",
    className: ""
  }] : isVideo ? [{
    key: "audio",
    label: "Add Audio",
    icon: Music,
    onClick: () => handleAudioClick(imageInfo.id),
    tooltip: "Add Audio to this video",
    className: isFirstImage ? "joyride-audio-option" : ""
  }, {
    key: "lipSync",
    label: "Lip Sync",
    icon: Music2,
    onClick: () => handleLipSyncClick(imageInfo),
    tooltip: "Add AI-generated audio to this video",
    className: ""
  }, {
    key: "mergeAudioAndVideo",
    label: "Merge Audio & Video",
    icon: MergeIcon,
    onClick: () => handleMergeAudioAndVideoClick(imageInfo),
    tooltip: "Merge this Video with Audio",
    className: ""
  }] : [{
    key: "useAsReference",
    label: "Use as Reference",
    icon: Lightbulb,
    onClick: () => handleUseAsReferenceClick(imageInfo.image),
    tooltip: "Use this image as a reference for generation",
    className: ""
  }, ...(handleQuickEditClick ? [{
    key: "quickEdit",
    label: "Quick Edit (New!)",
    icon: Wand2,
    onClick: () => handleQuickEditClick(imageInfo.id),
    tooltip: "Make quick changes to this image",
    className: ""
  }] : []), {
    key: "video",
    label: "Animate",
    icon: Video,
    onClick: () => handleVideoClick(imageInfo),
    tooltip: "Animate this image",
    className: isFirstImage ? "joyride-animate-option" : ""
  }]), ...(isOwnedByUser && isVideo ? [{
    key: "delete",
    label: "Delete",
    icon: Trash2,
    onClick: () => prepareForDelete(imageInfo.id),
    tooltip: "Delete",
    className: isFirstImage ? "joyride-delete-option" : ""
  }] : isOwnedByUser && isAudio ? [{
    key: "delete",
    label: "Delete",
    icon: Trash2,
    onClick: () => prepareForDelete(imageInfo.id),
    tooltip: "Delete",
    className: isFirstImage ? "joyride-delete-option" : ""
  }] : isOwnedByUser && !isVideo && !isAudio ? [{
    key: "competitions",
    label: "Enter Competition",
    icon: Trophy,
    onClick: () => handleCompetitionsClick(imageInfo.id),
    tooltip: "Enter a Competition and win gold!",
    className: isFirstImage ? "joyride-competition-option" : ""
  }, {
    key: "outpainting",
    label: "Outpainting",
    icon: Move,
    onClick: () => handleOutpaintingClick(imageInfo),
    tooltip: "Outpainting allows you to extend an image in any direction beyond its boundaries such as to fill in a full body picture from just a half body.",
    className: isFirstImage ? "joyride-outpainting-option" : ""
  }, {
    key: "inpaint",
    label: "Spot Edit (Inpaint)",
    icon: Edit,
    onClick: () => handleInpaintClick(imageInfo.id),
    tooltip: "Edit part of an image keeping the rest the same.",
    className: ""
  }, {
    key: "faceswap",
    label: "Face Swap",
    icon: RefreshCw,
    onClick: () => handleFaceSwapClick(imageInfo.id),
    tooltip: "Swap a Face in the Image with an uploaded Face",
    className: isFirstImage ? "joyride-swap-faces-option" : ""
  }, {
    key: "delete",
    label: "Delete",
    icon: Trash2,
    onClick: () => prepareForDelete(imageInfo.id),
    tooltip: "Delete",
    className: isFirstImage ? "joyride-delete-option" : ""
  }, {
    key: "upscale",
    label: "Upscale",
    icon: Expand,
    onClick: () => handleUpscaleClick(imageInfo.id),
    tooltip: "Upscale an image resolution upto 4x",
    className: isFirstImage ? "joyride-upscale-option" : ""
  }, {
    key: "adetailer",
    label: "Fix Faces",
    icon: SmilePlus,
    onClick: () => handleADetailerClick(imageInfo.id),
    tooltip: "Refine Faces only, keeping the rest of the image the same",
    className: isFirstImage ? "joyride-refine-faces-option" : ""
  }, {
    key: "bgremoval",
    label: "Remove Background",
    icon: ImageMinus,
    onClick: () => handleBGRemovalClick(imageInfo.id),
    tooltip: "Remove the Background from the Image",
    className: isFirstImage ? "joyride-remove-bg-option" : ""
  }] : [])], [isFirstImage, isAudio, isVideo, handleQuickEditClick, isOwnedByUser, imageInfo, handleLipSyncClick, handleMergeAudioAndVideoClick, handleAudioClick, handleUseAsReferenceClick, handleVideoClick, prepareForDelete, handleCompetitionsClick, handleOutpaintingClick, handleInpaintClick, handleFaceSwapClick, handleUpscaleClick, handleADetailerClick, handleBGRemovalClick, isAdmin, handleToggleNSFW]);

  // Hidden container for measuring controls
  const hiddenContainerRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const updateControls = () => {
      if (!controlsRef.current) return;

      // Calculate available space for expandable controls
      const containerWidth = controlsRef.current.offsetWidth;

      // Account for fixed controls and spacing
      let fixedControlsWidth = Object.values(fixedControlRefs.current).reduce((total, el) => {
        return total + (el?.offsetWidth || 0);
      }, 0);

      // Add extra buffer for spacing between items
      fixedControlsWidth += fixedControls.length * 8;

      // Reserve space for the overflow button and side padding - Increased buffer
      const reservedSpace = fixedControlsWidth + 100;

      // Available space for expandable controls
      const availableSpace = Math.max(0, containerWidth - reservedSpace);

      // Calculate which controls fit
      let currentWidth = 0;
      const visible: any[] = [];
      const overflow: any[] = [];
      expandableControls.forEach(control => {
        const el = expandableControlRefs.current[control.key];

        // Get actual width with a safety buffer
        // When labels are shown, add more space based on label length
        let width = el?.offsetWidth || 0;
        if (showLabel) {
          // Ensure at least a minimum width for labels
          width = Math.max(width, control.label ? control.label.length * 7 + 40 : 80);
        }
        width += 12; // Safety margin

        if (currentWidth + width <= availableSpace) {
          visible.push(control);
          currentWidth += width;
        } else {
          overflow.push(control);
        }
      });
      setVisibleControls(visible);
      setOverflowControls(overflow);
    };

    // Use a reliable approach for measurements with a slightly longer delay
    // to ensure all DOM elements are fully rendered
    const timeoutId = setTimeout(updateControls, 100);
    const debouncedUpdateControls = debounce(updateControls, 150);
    window.addEventListener("resize", debouncedUpdateControls);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", debouncedUpdateControls);
    };
  }, [expandableControls, fixedControls, showLabel, isOwnedByUser, isAdmin, handleToggleNSFW]);
  return <div className={"w-full !rounded-2xl flex justify-between items-center flex-col"} ref={controlsRef} data-sentry-component="ControlsContainer" data-sentry-source-file="index.tsx">
            <TooltipProvider data-sentry-element="TooltipProvider" data-sentry-source-file="index.tsx">
                {/* Hidden container for measuring controls */}
                <div ref={hiddenContainerRef} className="absolute invisible h-0 overflow-hidden opacity-0 pointer-events-none">
                    {/* Render fixed controls for measurement */}
                    <div className="flex items-center gap-2">
                        {fixedControls.map(control => <div key={control.key} ref={el => {
            fixedControlRefs.current[control.key] = el;
          }}>
                                {control.render(null)}
                            </div>)}
                    </div>

                    {/* Render expandable controls for measurement */}
                    <div className="flex items-center gap-2">
                        {expandableControls.map(control => <ControlButton key={control.key} ref={el => {
            expandableControlRefs.current[control.key] = el;
          }} icon={control.icon} onClick={control.onClick} tooltip={control.tooltip} label={control.label} showLabel={showLabel} />)}
                    </div>
                </div>

                <div className={"flex items-center rounded-2xl p-2 bg-slate-800 w-full gap-1 justify-between border-t border-slate-800"}>
                    {/* Left Side - Fixed Controls */}
                    <div className="flex items-center space-x-2 flex-shrink-0">
                        <div key={fixedControls[0].key}>
                            {fixedControls[0].render(null)}
                        </div>
                    </div>

                    {/* Center - Expanded Controls */}
                    <div className={`flex flex-1 items-center justify-start overflow-x-auto ${showLabel ? "gap-1 sm:gap-2" : "gap-0.5"} pl-2 pr-2`}>
                        {visibleControls.map(control => {
            if (control.key === "download") {
              return <a href={imageInfo.image} download target="_blank" key={control.key} className="flex-shrink-0" aria-label="Download Image">
                                        <ControlButton icon={control.icon} onClick={control.onClick} tooltip={control.tooltip} label={control.label} showLabel={showLabel} />
                                    </a>;
            }
            return <ControlButton key={control.key} icon={control.icon} onClick={control.onClick} tooltip={control.tooltip} label={control.label} showLabel={showLabel} className="flex-shrink-0" />;
          })}
                    </div>

                    {/* Right Side - Fixed Controls */}
                    <div className="flex items-center space-x-2 flex-shrink-0">
                        {fixedControls.slice(1).map(control => <div key={control.key}>{control.render(null)}</div>)}

                        {/* Right Side - Overflow Menu */}

                        <div className="flex items-center justify-center gap-2">
                            {overflowControls.length > 0 && <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant="ghost" size="icon" className={`!h-8 !w-8 !text-slate-200 hover:!text-sky-400 hover:!bg-slate-800/50 transition-colors duration-200 ${isFirstImage ? "joyride-ellipsis-button" : ""}`} ref={popoverButtonRef} aria-label="More Options">
                                            <MoreHorizontal className="h-4 w-4" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto z-[1999] border border-slate-700 !p-2 grid gap-1">
                                        <div className="grid gap-1">
                                            {overflowControls.map(control => {
                    if (control.key === "download") {
                      return <a href={imageInfo.image} download target="_blank" key={control.key} aria-label="Download Image">
                                                            <Button variant="ghost" size="sm" className="w-full !h-10 !justify-start !text-slate-200 hover:!text-sky-400 hover:!bg-slate-700/50 transition-all duration-200 hover:!scale-[1.02] !py-1.5 !px-2" onClick={control.onClick} aria-label={control.label}>
                                                                <control.icon className="h-4 w-4 mr-2 flex-shrink-0" />
                                                                <span className="truncate">
                                                                    {control.label}
                                                                </span>
                                                            </Button>
                                                        </a>;
                    } else if (control.key === "delete") {
                      return <Button variant="ghost" size="sm" className="w-full !h-10 !justify-start !text-red-400 hover:!text-red-600 hover:!bg-red-600/25 transition-all duration-200 hover:!scale-[1.02] !py-1.5 !px-2" onClick={control.onClick} aria-label={control.label} key={control.key}>
                                                            <control.icon className="h-4 w-4 mr-2 flex-shrink-0" />
                                                            <span className="truncate">
                                                                {control.label}
                                                            </span>
                                                        </Button>;
                    } else if (control.key === "toggleNsfw") {
                      return <Button key={control.key} variant="ghost" size="sm" className="w-full !h-10 !justify-start hover:!bg-slate-700/50 transition-all duration-200 hover:!scale-[1.02] !py-1.5 !px-2 !text-red-500 hover:!text-red-400" onClick={control.onClick} aria-label={control.label}>
                                                            <control.icon className="h-4 w-4 mr-2 flex-shrink-0" />
                                                            <span className="truncate">
                                                                {control.label}
                                                            </span>
                                                        </Button>;
                    }
                    return <Button key={control.key} variant="ghost" size="sm" className="w-full !h-10 !justify-start !text-slate-200 hover:!text-sky-400 hover:!bg-slate-700/50 transition-all duration-200 hover:!scale-[1.02] !py-1.5 !px-2" onClick={control.onClick} aria-label={control.label}>
                                                        <control.icon className="h-4 w-4 mr-2 flex-shrink-0" />
                                                        <span className="truncate">
                                                            {control.label}
                                                        </span>
                                                    </Button>;
                  })}
                                        </div>
                                    </PopoverContent>
                                </Popover>}
                        </div>
                    </div>
                </div>

                {/* Visibility Message */}
                {isOwnedByUser && <div className="text-xs text-slate-400 px-2 pb-2 text-center md:hidden">
                        <span className="inline-flex items-center gap-1">
                            {imageInfo.public ? <Eye className="h-3 w-3 text-sky-500" /> : <EyeOff className="h-3 w-3" />}
                            {imageInfo.public ? "Visible to all" : "Only visible to you"}{" "}
                            - Tap eye icon to change
                        </span>
                    </div>}
            </TooltipProvider>
        </div>;
};
export default ControlsContainer;